/**
 * Canonical peril type options — single source of truth for the entire app.
 * Values match the perilTypeEnum in shared/schema.ts.
 */
export const PERIL_TYPES = [
  { value: "fire", label: "Fire" },
  { value: "water", label: "Water Damage" },
  { value: "wind", label: "Wind Damage" },
  { value: "hail", label: "Hail Damage" },
  { value: "wind_hail", label: "Wind & Hail" },
  { value: "tornado", label: "Tornado" },
  { value: "hurricane", label: "Hurricane" },
  { value: "lightning", label: "Lightning" },
  { value: "smoke", label: "Smoke Damage" },
  { value: "flood", label: "Flood" },
  { value: "earthquake", label: "Earthquake" },
  { value: "theft", label: "Theft" },
  { value: "vandalism", label: "Vandalism" },
  { value: "other", label: "Other" },
] as const;

/** Peril type enum values as a tuple — usable directly with z.enum(). */
export const PERIL_TYPE_VALUES = PERIL_TYPES.map(p => p.value) as unknown as readonly [string, ...string[]];

/**
 * US state options — single source of truth for state dropdowns.
 * Values are two-letter USPS abbreviations matching propertyState in the claims schema.
 */
export const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
] as const;

import { roleDisplayName, ROLE_LABELS } from '@shared/roleLabels';

/**
 * Formats a snake_case or kebab-case string into Title Case.
 * @example "on_hold" -> "On Hold"
 * @example "wind_hail" -> "Wind & Hail" (special handling for peril types)
 */
export function formatEnum(value: string | undefined): string {
  if (!value) return "—";
  
  // Check peril types for canonical label
  const peril = PERIL_TYPES.find(p => p.value === value);
  if (peril) return peril.label;
  
  // Special handling for specific enums if needed
  if (value === "on_hold") return "On Hold";
  if (value === "proposal_sent") return "Proposal Sent";
  if (value === "ai_search") return "AI Search";
  if (value === "google_search") return "Google Search";
  if (value === "sponsored_ad") return "Sponsored Ad";

  // Role type labels — delegate to canonical map; only intercept known slugs
  if (value in ROLE_LABELS) return roleDisplayName(value);

  // Default: replace underscores/hyphens with spaces and capitalize
  return value
    .split(/[_-]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}
